import { useCallback, useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Loader2, Minus } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const Delta = ({ hms, pct, seconds, testid }) => {
  if (!seconds) return <span className="text-slate-400 dark:text-slate-500" data-testid={testid}><Minus className="inline h-3 w-3" /></span>;
  const up = seconds > 0;
  const Icon = up ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${up ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700 dark:text-rose-400"}`}
      data-testid={testid}>
      <Icon className="h-3 w-3" />{hms}
      {pct !== null && pct !== undefined && <span className="text-xs font-normal">({up ? "+" : ""}{pct}%)</span>}
    </span>
  );
};

/** Productivity → Compare. Whole-week or whole-month comparison for every
 *  tracked person, independent of the page's date range. */
export function ProdComparePanel({ team, anchor }) {
  const [mode, setMode] = useState("week");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = `mode=${mode}&anchor=${anchor}${team ? `&team=${encodeURIComponent(team)}` : ""}`;
      const { data: d } = await api.get(`/productivity/compare?${q}`);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load the comparison");
    } finally { setLoading(false); }
  }, [mode, anchor, team]);
  useEffect(() => { load(); }, [load]);

  const t = data?.totals;

  return (
    <div className="space-y-3" data-testid="prod-compare-panel">
      <div className="flex flex-wrap items-center gap-2">
        {[["week", "Week over week"], ["month", "Month over month"]].map(([m, label]) => (
          <Button key={m} size="sm" variant={mode === m ? "default" : "outline"}
            onClick={() => setMode(m)} data-testid={`prod-compare-mode-${m}`}>{label}</Button>
        ))}
        {data && (
          <span className="text-xs text-slate-500 dark:text-slate-400" data-testid="prod-compare-windows">
            {data.current.label} vs {data.previous.label}{team ? ` · ${team}` : " · all teams"}
          </span>
        )}
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>

      {data?.previous?.partial && (
        <div className="rounded-md border-l-4 border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300"
          data-testid="prod-compare-partial">
          History in Conduit only starts {data.previous.history_from}, so the earlier
          {mode === "month" ? " month" : " week"} is incomplete — read those percentages with care.
        </div>
      )}

      {(data?.movers?.up?.length || data?.movers?.down?.length) ? (
        <Card className="p-3" data-testid="prod-compare-movers">
          <div className="mb-2 text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">Biggest movers — productive hours</div>
          <div className="grid gap-3 sm:grid-cols-2">
            {[["Up most", "up", "text-emerald-700 dark:text-emerald-400", ArrowUp],
              ["Down most", "down", "text-rose-700 dark:text-rose-400", ArrowDown]].map(([label, key, tone, Icon]) => (
              <div key={key} data-testid={`prod-compare-movers-${key}`}>
                <div className={`mb-1 flex items-center gap-1 text-xs font-semibold ${tone}`}>
                  <Icon className="h-3 w-3" />{label}
                </div>
                {(data.movers[key] || []).length === 0
                  ? <div className="text-xs text-slate-400 dark:text-slate-500">Nobody</div>
                  : (data.movers[key] || []).map((m) => (
                    <div key={m.name} className="flex items-baseline justify-between gap-2 py-0.5 text-sm">
                      <span className="truncate">{m.name} <span className="text-xs text-slate-400 dark:text-slate-500">{m.team}</span></span>
                      <span className={`shrink-0 font-semibold ${tone}`}>
                        {m.delta_hms}{m.delta_pct !== null && m.delta_pct !== undefined ? ` (${m.delta_pct > 0 ? "+" : ""}${m.delta_pct}%)` : ""}
                        <span className="ml-1 font-mono text-xs font-normal text-slate-400 dark:text-slate-500">{m.prev_hms} → {m.now_hms}</span>
                      </span>
                    </div>
                  ))}
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {t && (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="prod-compare-totals">
          {[["Total work time", t.work, "work"], ["Productive", t.productive, "productive"]].map(([label, v, id]) => (
            <Card key={id} className="p-3" data-testid={`prod-compare-total-${id}`}>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
              <div className="flex items-baseline gap-2">
                <span className="text-xl font-bold text-slate-900 dark:text-slate-50">{v.now_hms}</span>
                <Delta hms={v.delta_hms} pct={v.delta_pct} seconds={v.delta_seconds} testid={`prod-compare-total-${id}-delta`} />
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">was {v.prev_hms}</div>
            </Card>
          ))}
        </div>
      )}

      <Card className="overflow-auto p-0" data-testid="prod-compare-teams-card">
        <table className="w-full text-left text-sm" data-testid="prod-compare-teams-table">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            <tr>
              <th className="px-3 py-2">Team</th>
              <th className="px-3 py-2 text-right">People</th>
              <th className="px-3 py-2 text-right">Total work time</th>
              <th className="px-3 py-2 text-right">Was</th>
              <th className="px-3 py-2 text-right">Change</th>
              <th className="px-3 py-2 text-right">Productive</th>
              <th className="px-3 py-2 text-right">Was</th>
              <th className="px-3 py-2 text-right">Change</th>
            </tr>
          </thead>
          <tbody>
            {(data?.teams || []).map((r) => (
              <tr key={r.team} className="border-t border-slate-100 dark:border-slate-800" data-testid={`prod-compare-team-${r.team.replace(/\s+/g, "-").toLowerCase()}`}>
                <td className="px-3 py-1.5 font-medium">{r.team}</td>
                <td className="px-3 py-1.5 text-right">{r.members}</td>
                <td className="px-3 py-1.5 text-right font-mono text-xs">{r.now_work_hms}</td>
                <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-400 dark:text-slate-500">{r.prev_work_hms}</td>
                <td className="px-3 py-1.5 text-right"><Delta hms={r.work_delta_hms} pct={r.work_delta_pct} seconds={r.work_delta_seconds} /></td>
                <td className="px-3 py-1.5 text-right font-mono text-xs">{r.now_productive_hms}</td>
                <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-400 dark:text-slate-500">{r.prev_productive_hms}</td>
                <td className="px-3 py-1.5 text-right"><Delta hms={r.productive_delta_hms} pct={r.productive_delta_pct} seconds={r.productive_delta_seconds} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="overflow-auto p-0">
        <table className="w-full text-left text-sm" data-testid="prod-compare-table">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
            <tr>
              <th className="px-3 py-2">Staff</th>
              <th className="px-3 py-2">Team</th>
              <th className="px-3 py-2 text-right" title="The monitoring tool's Time Cards → Total work time">Total work time</th>
              <th className="px-3 py-2 text-right">Was</th>
              <th className="px-3 py-2 text-right">Change</th>
              <th className="px-3 py-2 text-right">Productive</th>
              <th className="px-3 py-2 text-right">Was</th>
              <th className="px-3 py-2 text-right">Change</th>
            </tr>
          </thead>
          <tbody>
            {(data?.people || []).map((p) => (
              <tr key={p.key} className="border-t border-slate-100 dark:border-slate-800" data-testid={`prod-compare-row-${p.key}`}>
                <td className="px-3 py-1.5 font-medium">{p.name}</td>
                <td className="px-3 py-1.5 text-slate-500 dark:text-slate-400">{p.team}</td>
                <td className="px-3 py-1.5 text-right font-mono text-xs">{p.now_work_hms}</td>
                <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-400 dark:text-slate-500">{p.prev_work_hms}</td>
                <td className="px-3 py-1.5 text-right">
                  <Delta hms={p.work_delta_hms} pct={p.work_delta_pct} seconds={p.work_delta_seconds} testid={`prod-compare-work-delta-${p.key}`} />
                </td>
                <td className="px-3 py-1.5 text-right font-mono text-xs">{p.now_productive_hms}</td>
                <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-400 dark:text-slate-500">{p.prev_productive_hms}</td>
                <td className="px-3 py-1.5 text-right">
                  <Delta hms={p.productive_delta_hms} pct={p.productive_delta_pct} seconds={p.productive_delta_seconds} testid={`prod-compare-prod-delta-${p.key}`} />
                </td>
              </tr>
            ))}
            {!loading && !(data?.people || []).length && (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-sm text-slate-500 dark:text-slate-400">Nothing tracked in either period.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
